import {a11yStandardKeys} from "./constants";
import {isMaltaWttcBuildingArea} from "./propertyTabs";

// Which number cells (KPIs) are shown at the top of a building area or a property, by a11y standard.
// `empty` keeps a grid slot free.

export type BuildingAreaKpi =
  | 'buildings'
  | 'floors'
  | 'shops'
  | 'accessibleRooms'
  | 'allergyRooms'
  | 'parkingSpaces'
  | 'toilets'
  | 'publicToilets'
  | 'playgrounds'
  | 'hotels'
  | 'venues'
  | 'empty'

const DEFAULT_BUILDING_AREA_KPIS: BuildingAreaKpi[] = ['floors', 'shops', 'accessibleRooms', 'allergyRooms']

const BUILDING_AREA_KPIS_BY_STANDARD: Record<string, BuildingAreaKpi[]> = {
  [a11yStandardKeys.shoppingMall]: ['buildings', 'parkingSpaces', 'toilets', 'shops'],
  [a11yStandardKeys.museum]: ['buildings', 'parkingSpaces', 'toilets'],
  [a11yStandardKeys.hotel]: ['buildings', 'accessibleRooms', 'parkingSpaces'],
  [a11yStandardKeys.office]: ['buildings', 'parkingSpaces', 'toilets'],
  [a11yStandardKeys.outdoorPlayground]: ['playgrounds', 'publicToilets', 'empty'],
}

// the building area mixes hotels and venues, so its standard does not describe it
const MALTA_WTTC_KPIS: BuildingAreaKpi[] = ['hotels', 'venues', 'empty']

export const getBuildingAreaKpis = (standardKey: string, buildingAreaId?: string | null): BuildingAreaKpi[] => {
  if (isMaltaWttcBuildingArea(buildingAreaId)) {
    return MALTA_WTTC_KPIS
  }
  return BUILDING_AREA_KPIS_BY_STANDARD[standardKey] ?? DEFAULT_BUILDING_AREA_KPIS
}

export type PropertyKpi =
  | 'floors'
  | 'totalRooms'
  | 'accessibleRooms'
  | 'allergyRooms'
  | 'parking'
  | 'toilets'
  | 'accessibleToilets'
  | 'shops'
  | 'attractions'
  | 'empty'

const DEFAULT_PROPERTY_KPIS: PropertyKpi[] = ['floors', 'totalRooms', 'accessibleRooms', 'allergyRooms']

const PROPERTY_KPIS_BY_STANDARD: Record<string, PropertyKpi[]> = {
  [a11yStandardKeys.shoppingMall]: ['floors', 'parking', 'accessibleToilets', 'shops'],
  [a11yStandardKeys.museum]: ['floors', 'parking', 'accessibleToilets'],
  [a11yStandardKeys.hotel]: ['floors', 'accessibleRooms', 'parking'],
  [a11yStandardKeys.office]: ['floors', 'parking', 'accessibleToilets'],
  [a11yStandardKeys.outdoorPlayground]: ['parking', 'empty', 'empty'],
  [a11yStandardKeys.cottage]: ['empty', 'empty', 'empty'],
  [a11yStandardKeys.restaurant]: ['accessibleToilets', 'empty', 'empty'],
  [a11yStandardKeys.camping]: ['accessibleToilets', 'empty', 'empty'],
  [a11yStandardKeys.serviceHouse]: ['accessibleToilets', 'empty', 'empty'],
  [a11yStandardKeys.attractionPark]: ['attractions', 'toilets', 'empty'],
}

export const getPropertyKpis = (standardKey: string): PropertyKpi[] => (
  PROPERTY_KPIS_BY_STANDARD[standardKey] ?? DEFAULT_PROPERTY_KPIS
)
